import { Component, computed, effect, ElementRef, input, output, signal, viewChildren, WritableSignal } from '@angular/core';

interface TOption<V extends string | number> {
  value: V;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'rid-pill-select',
  standalone: true,
  imports: [],
  templateUrl: './rid-pill-select.html',
  host: {
    class: 'block w-full',
  },
})
export class RidPillSelect<T extends string | number> {
  options = input.required<readonly TOption<T>[]>();
  valueSignal = input<WritableSignal<T> | undefined>(undefined);
  disabled = input<boolean>(false);
  ariaLabel = input<string>();

  private readonly pillGapRem = 0.5;
  private internalValue = signal<T | undefined>(undefined);
  private highlightFocusVisible = signal(false);
  private firstOptionValue = computed(() => {
    const firstOption = this.options()[0];
    if (!firstOption) {
      throw new Error('rid-pill-select requires at least one option');
    }

    return firstOption.value;
  });
  value = computed(() => this.valueSignal()?.() ?? this.internalValue() ?? this.firstOptionValue());

  valueChange = output<T>();

  optionButtons = viewChildren<ElementRef<HTMLButtonElement>>('optionButton');

  selectedIndex = computed(() => this.options().findIndex((option) => option.value === this.value()));
  hasHighlight = computed(() => this.selectedIndex() !== -1);

  columns = computed(() => {
    const optionCount = Math.max(1, this.options().length);

    return optionCount <= 3 ? optionCount : Math.ceil(Math.sqrt(optionCount));
  });
  rows = computed(() => Math.ceil(Math.max(1, this.options().length) / this.columns()));
  gridTemplateColumns = computed(() => `repeat(${this.columns()}, minmax(0, 1fr))`);

  highlightWidth = computed(() => {
    const columns = this.columns();

    return columns === 1 ? '100%' : `calc((100% - ${(columns - 1) * this.pillGapRem}rem) / ${columns})`;
  });
  highlightHeight = computed(() => {
    const rows = this.rows();

    return rows === 1 ? '100%' : `calc((100% - ${(rows - 1) * this.pillGapRem}rem) / ${rows})`;
  });
  highlightTransform = computed(() => {
    const selectedIndex = this.selectedIndex();
    if (selectedIndex <= 0) {
      return 'translate(0, 0)';
    }

    const columns = this.columns();
    const col = selectedIndex % columns;
    const row = Math.floor(selectedIndex / columns);
    const x = col === 0 ? '0px' : `calc(${col} * (100% + ${this.pillGapRem}rem))`;
    const y = row === 0 ? '0px' : `calc(${row} * (100% + ${this.pillGapRem}rem))`;

    return `translate(${x}, ${y})`;
  });

  constructor() {
    effect(() => {
      const options = this.options();
      const firstOption = options[0];

      if (!firstOption) {
        return;
      }

      const valueSignal = this.valueSignal();
      const selectedValue = valueSignal?.() ?? this.internalValue();
      const hasSelectedValue = options.some((option) => option.value === selectedValue);

      if (hasSelectedValue) {
        return;
      }

      if (valueSignal) {
        valueSignal.set(firstOption.value);
      } else {
        this.internalValue.set(firstOption.value);
      }
    });
  }

  isSelected(value: T) {
    return this.value() === value;
  }

  optionClasses(option: TOption<T>) {
    const baseClasses =
      'relative z-10 w-full min-w-0 rounded-xl bg-transparent px-4 py-2 text-center text-sm font-medium whitespace-nowrap transition-colors outline-none select-none';

    if (this.disabled() || option.disabled) {
      return `${baseClasses} cursor-not-allowed text-slate-300`;
    }

    if (this.isSelected(option.value)) {
      return `${baseClasses} text-slate-900`;
    }

    return `${baseClasses} cursor-pointer text-slate-500 hover:bg-white/70 hover:text-slate-700`;
  }

  select(option: TOption<T>) {
    if (this.disabled() || option.disabled || option.value === this.value()) {
      return;
    }

    const valueSignal = this.valueSignal();
    if (valueSignal) {
      valueSignal.set(option.value);
    } else {
      this.internalValue.set(option.value);
    }

    this.valueChange.emit(option.value);
  }

  tabIndexFor(index: number) {
    return this.activeIndex() === index ? 0 : -1;
  }

  onFocus(event: FocusEvent) {
    const target = event.currentTarget;
    this.highlightFocusVisible.set(target instanceof HTMLButtonElement && target.matches(':focus-visible'));
  }

  onBlur() {
    this.highlightFocusVisible.set(false);
  }

  highlightClasses() {
    const baseClasses =
      'rounded-xl bg-white shadow-sm ring-1 ring-slate-200 transition-[transform,width,height,opacity,box-shadow] duration-[180ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none';

    if (!this.highlightFocusVisible()) {
      return baseClasses;
    }

    return `${baseClasses} [box-shadow:0_0_0_2px_rgb(15_23_42_/_0.18),0_0_0_4px_rgb(255_255_255_/_0.95),0_1px_2px_0_rgb(15_23_42_/_0.08)]`;
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (this.disabled()) {
      return;
    }

    if (event.key === 'Home') {
      const nextIndex = this.firstEnabledIndex();
      if (nextIndex !== -1) {
        event.preventDefault();
        this.focusIndex(nextIndex);
        this.select(this.options()[nextIndex]);
      }
      return;
    }

    if (event.key === 'End') {
      const nextIndex = this.lastEnabledIndex();
      if (nextIndex !== -1) {
        event.preventDefault();
        this.focusIndex(nextIndex);
        this.select(this.options()[nextIndex]);
      }
      return;
    }

    const direction =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;

    if (!direction) {
      return;
    }

    const nextIndex = this.findEnabledIndex(index, direction);
    if (nextIndex === index) {
      return;
    }

    event.preventDefault();
    this.focusIndex(nextIndex);
    this.select(this.options()[nextIndex]);
  }

  private activeIndex() {
    const selectedIndex = this.options().findIndex((option) => !option.disabled && option.value === this.value());
    return selectedIndex !== -1 ? selectedIndex : this.firstEnabledIndex();
  }

  private firstEnabledIndex() {
    return this.options().findIndex((option) => !option.disabled);
  }

  private lastEnabledIndex() {
    for (let index = this.options().length - 1; index >= 0; index--) {
      if (!this.options()[index]?.disabled) {
        return index;
      }
    }

    return -1;
  }

  private findEnabledIndex(startIndex: number, direction: -1 | 1) {
    const options = this.options();
    if (!options.length) {
      return startIndex;
    }

    let nextIndex = startIndex;
    for (let remaining = options.length; remaining > 0; remaining--) {
      nextIndex = (nextIndex + direction + options.length) % options.length;
      if (!options[nextIndex]?.disabled) {
        return nextIndex;
      }
    }

    return startIndex;
  }

  private focusIndex(index: number) {
    this.optionButtons()[index]?.nativeElement.focus();
  }
}
